import { useEffect, useState } from 'react';
import { Check, Paperclip } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Board = { id: string; name: string };
type StatusCol = { columnId: string; title: string; distinct: number; lifecycleHits: number; values: Record<string, number> };
type Preview = {
  totalItems: number; matched: number; unmatched: number; wouldCreate: number;
  matchSources: Record<string, number>; statusColumns: StatusCol[];
  chosenColumnId: string | null; sampleUnmatched: string[];
};
type Import = { applied: boolean; matched: number; withStatus: number; updated: number; created: number; statusCounts: Record<string, number> };

/**
 * Herramienta super-manager: importa el status de ciclo de vida (Lead→Archived)
 * desde el board de Claims de Monday. Preview no escribe; Importar sí. Colapsado
 * por defecto. Tras importar, refresca la lista para que la barra de status pinte.
 */
export function ClaimsBoardSync({ onImported }: { onImported?: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  // CORTE DE MONDAY (2026-08-24): esta herramienta LEE el board de Claims. Con el sync cortado
  // no hay board que leer, así que desaparece entera en vez de dejar botones que dan 409.
  // `null` mientras no se sabe: pintarla y esconderla al segundo siguiente es peor que esperar.
  const [cortado, setCortado] = useState<boolean | null>(null);
  useEffect(() => {
    api.get<{ disconnected?: boolean }>('/api/sync/connection')
      .then((c) => setCortado(!!c.disconnected))
      .catch(() => setCortado(false));
  }, []);
  const [boards, setBoards] = useState<Board[]>([]);
  // Board de Claims de Monday (lo dio el dueño). Editable por si cambia.
  const [boardId, setBoardId] = useState('3991743813');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<Import | null>(null);
  const [createMissing, setCreateMissing] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function call<T>(fn: () => Promise<T>, tag: string): Promise<T | null> {
    setBusy(tag); setErr(null);
    try { return await fn(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); return null; }
    finally { setBusy(''); }
  }

  async function loadBoards() {
    const r = await call(() => api.get<{ boards: Board[] }>('/api/sync/monday/boards'), 'boards');
    if (r) setBoards(r.boards);
  }
  async function doPreview() {
    setResult(null);
    const r = await call(() => api.post<Preview>('/api/sync/monday/claims-board/preview', { boardId }), 'preview');
    if (r) setPreview(r);
  }
  async function doImport() {
    const r = await call(() => api.post<Import>('/api/sync/monday/claims-board/import', { boardId, apply: true, createMissing }), 'import');
    if (r) { setResult(r); onImported?.(); }
  }
  async function doSubscribe() {
    const r = await call(() => api.post<{ created?: number; existing?: number }>('/api/sync/webhook-subscribe', { boardId }), 'subscribe');
    if (r) setErr(t('cbsync.subscribed'));
  }
  const [files, setFiles] = useState<{ running: boolean; done: number; total: number; downloaded: number; skipped: number; errors: number } | null>(null);
  async function pollFiles() {
    try {
      const s = await api.get<{ running: boolean; done: number; total: number; downloaded: number; skipped: number; errors: number }>('/api/sync/monday/subitem-files/status');
      setFiles(s);
      if (s.running) setTimeout(pollFiles, 4000);
    } catch { /* ignore */ }
  }
  async function doImportFiles() {
    if (!window.confirm(t('cbsync.filesConfirm'))) return;
    await call(() => api.post('/api/sync/monday/subitem-files', {}), 'files');
    pollFiles();
  }

  const chosen = preview?.statusColumns.find((s) => s.columnId === preview.chosenColumnId);

  if (cortado !== false) return null;

  return (
    <div className="card">
      <h2 className="card-title" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, margin: 0 }} onClick={() => setOpen((o) => !o)}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
        {t('cbsync.title')}
      </h2>

      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ maxWidth: 160 }}
              placeholder="Board ID"
              value={boardId}
              onChange={(e) => setBoardId(e.target.value.trim())}
            />
            <button className="btn ghost sm" disabled={busy === 'boards'} onClick={loadBoards}>
              {busy === 'boards' ? '…' : t('cbsync.loadBoards')}
            </button>
            {boards.length > 0 && (
              <select className="input" style={{ maxWidth: 320 }} value={boardId} onChange={(e) => setBoardId(e.target.value)}>
                <option value="">{t('cbsync.pickBoard')}</option>
                {boards.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.id})</option>)}
              </select>
            )}
            <button className="btn ghost sm" disabled={!boardId || busy === 'preview'} onClick={doPreview}>
              {busy === 'preview' ? '…' : t('cbsync.preview')}
            </button>
            <button className="btn sm" disabled={!boardId || !preview || busy === 'import'} onClick={doImport}>
              {busy === 'import' ? '…' : t('cbsync.import')}
            </button>
            <button className="btn ghost sm" disabled={!boardId || busy === 'subscribe'} onClick={doSubscribe} title={t('cbsync.subscribeHelp')}>
              {busy === 'subscribe' ? '…' : t('cbsync.subscribe')}
            </button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)' }} title={t('cbsync.createMissingHelp')}>
            <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
            {t('cbsync.createMissing')}{preview && preview.wouldCreate > 0 ? ` (${preview.wouldCreate})` : ''}
          </label>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cbsync.twoWayHint')}</div>

          {/* Import de archivos de subitems (corre en el servidor, resumible). */}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn ghost sm" disabled={busy === 'files' || files?.running} onClick={doImportFiles}>
              {files?.running ? '…' : <><Paperclip size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cbsync.importFiles')}</>}
            </button>
            {files && (
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {files.running ? `${files.done}/${files.total} claims · ` : <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />{' '}</>}
                {t('cbsync.filesDownloaded', { n: String(files.downloaded) })}
                {files.skipped ? ` · ${files.skipped} skip` : ''}{files.errors ? ` · ${files.errors} err` : ''}
              </span>
            )}
          </div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cbsync.filesHint')}</div>

          {err && <div className="badge red">{err}</div>}

          {preview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--fs-sm)' }}>
              <div className="muted">{t('cbsync.dryRun')}</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span><strong>{preview.totalItems}</strong> {t('cbsync.totalItems')}</span>
                <span style={{ color: 'var(--green)' }}><strong>{preview.matched}</strong> {t('cbsync.matched')}</span>
                <span className="muted"><strong>{preview.unmatched}</strong> {t('cbsync.unmatched')}</span>
                {preview.wouldCreate > 0 && <span style={{ color: 'var(--accent)' }}><strong>{preview.wouldCreate}</strong> {t('cbsync.wouldCreate')}</span>}
              </div>
              {chosen && (
                <div>
                  <div className="muted" style={{ marginTop: 4 }}>{t('cbsync.chosenCol')}: <strong style={{ color: 'var(--ink)' }}>{chosen.title}</strong> ({chosen.lifecycleHits} en ciclo)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {Object.entries(chosen.values).sort((a, b) => b[1] - a[1]).map(([label, n]) => (
                      <span key={label} className="badge gray">{label}: {n}</span>
                    ))}
                  </div>
                </div>
              )}
              {preview.sampleUnmatched.length > 0 && (
                <details>
                  <summary className="muted">{t('cbsync.sampleUnmatched')} ({preview.unmatched})</summary>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {preview.sampleUnmatched.map((s, i) => <li key={i} className="muted">{s}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          {result && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
              <span className="badge green"><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cbsync.applied')}</span>
              <span style={{ color: 'var(--green)' }}><strong>{result.updated}</strong> {t('cbsync.updated')}</span>
              {result.created > 0 && <span style={{ color: 'var(--accent)' }}><strong>{result.created}</strong> {t('cbsync.created')}</span>}
              <span className="muted"><strong>{result.matched}</strong> {t('cbsync.matched')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
